import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token management
const ADMIN_TOKEN_KEY = 'shiva_shakti_admin_token';

export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (token) => localStorage.setItem(ADMIN_TOKEN_KEY, token);
export const removeAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

// Add auth header to requests
const getAuthHeaders = () => {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Admin Auth
export const adminLogin = async (username, password) => {
  const response = await api.post('/admin/login', { username, password });
  if (response.data.token) {
    setAdminToken(response.data.token);
  }
  return response.data;
};

export const verifyAdmin = async () => {
  const token = getAdminToken();
  if (!token) return false;
  try {
    const response = await api.get('/admin/verify', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data.valid;
  } catch {
    removeAdminToken();
    return false;
  }
};

export const adminLogout = () => {
  removeAdminToken();
};

// File Upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload', formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Products
export const getProducts = async (category = null, featured = null) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (featured !== null) params.append('featured', featured);
  const response = await api.get(`/products?${params.toString()}`);
  return response.data;
};

export const getProduct = async (productId) => {
  const response = await api.get(`/products/${productId}`);
  return response.data;
};

export const createProduct = async (productData) => {
  const response = await api.post('/products', productData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateProduct = async (productId, productData) => {
  const response = await api.put(`/products/${productId}`, productData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await api.delete(`/products/${productId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// Categories
export const getCategories = async () => {
  const response = await api.get('/categories');
  return response.data;
};

// Checkout
export const createCheckoutSession = async (checkoutData) => {
  const response = await api.post('/checkout/create-session', checkoutData);
  return response.data;
};

export const getCheckoutStatus = async (sessionId) => {
  const response = await api.get(`/checkout/status/${sessionId}`);
  return response.data;
};

// Orders
export const getOrder = async (orderId) => {
  const response = await api.get(`/orders/${orderId}`);
  return response.data;
};

// Contact
export const submitContact = async (messageData) => {
  const response = await api.post('/contact', messageData);
  return response.data;
};

// Shipping Config
export const getShippingConfig = async () => {
  const response = await api.get('/shipping-config');
  return response.data;
};

export const updateShippingConfig = async (configData) => {
  const response = await api.put('/shipping-config', configData, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const calculateShipping = async (country, subtotal) => {
  const response = await api.post('/calculate-shipping', { country, subtotal });
  return response.data;
};

// Seed products
export const seedProducts = async () => {
  const response = await api.post('/seed-products');
  return response.data;
};

export default api;
